export type SubscriptionPackagePricing = {
    yearly_price: string;
    monthly_price: string;
    standard_yearly_price?: string;
    standard_monthly_price?: string;
    has_custom_price?: boolean;
    enable_field_seats: boolean;
    yearly_field_price: string;
    monthly_field_price: string;
    enable_light_seats: boolean;
    yearly_light_price: string;
    monthly_light_price: string;
};

// The billing features of the integration providers, always orderable in every package
export const INTEGRATION_FEATURES = [
    'exact_integration',
    'snelstart_integration',
    'moneybird_integration',
    'eboekhouden_integration',
    'asperion_integration',
    'mkg_integration',
];

export type AdditionalModule = {
    feature: string;
    feature_translation: string;
    description?: string | null;
    image?: string | null;
    price_type: 'per_unit' | 'per_seat';
    price_type_translation: string;
    flat_price: string;
    price_per_seat: string;
    enable_threshold: boolean;
    threshold: number;
    price_above_threshold: string;
    ordering?: number;
    is_highlighted?: boolean;
    is_active: boolean;
}

export type SubscriptionPackage = SubscriptionPackagePricing & {
    id: string;
    key: string;
    name: string;
    explanation_nl: string;
    highlighted_features_nl: string;
    highlighted_features_nl_list: string[];
    is_best_choice: boolean;
    is_view_only?: boolean;
    main_features: string[];
    sub_features: string[];
    modules: string[];
    module_fields?: AdditionalModule[];
    seat_limit: number | null;
    is_active: boolean;
};

export type ImplementationPackage = {
    id: number;
    name: string;
    description?: string | null;
    image?: string | null;
    price: string;
    ordering?: number;
    is_active: boolean;
};

export type SubscriptionLine = {
    start_date: string;
    end_date: string;
    type: "seats" | "field_seats" | "light_seats" | "additional_module" | "implementation" | "discount";
    product_name: string | null;
    quantity: number;
    price: string;
    display_price?: string;
    price_type: "per_seat" | "per_unit" | "discount_percentage";
    seats_threshold: number | null;
    price_above_threshold: string | null;
    is_active: boolean;
};

export type Subscription = {
    id: number | null,
    is_trial: boolean;
    is_provisional?: boolean;
    duration: 'trial' | 'monthly' | 'yearly';
    seats: { normal: string, field?: string, light?: string };
    additional_modules?: { name: string }[] | null;
    implementation_package?: number | null;
    package: string;
    start_date: string | null;
    end_date: string | null;
    status: string | null;
    cancellation_date?: string;
    cancellation_reason?: string;
    cancellation_remarks?: string;
    subscription_lines?: SubscriptionLine[];
};

export type SubscriptionData = Subscription & {
    pending_subscription?: Subscription;
};

export const createSubscriptionData = (currentPackage?: string, seats?: string): SubscriptionData => ({
    id: null,
    is_trial: false,
    duration: 'yearly',
    seats: { normal: seats ?? '1', field: '0', light: '0' },
    package: currentPackage ?? 'essential',
    start_date: null,
    end_date: null,
    status: null,
});

export type BillingData = {
    company_name: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    invoice_email: string | null;
    street: string | null;
    house_number: string | null;
    postal_code: string | null;
    city: string | null;
    country: string | null;
    vat_number: string | null;
    stripe_customer_id: string | null;
};

export const createBillingData = () => ({
    company_name: null,
    first_name: null,
    last_name: null,
    email: null,
    invoice_email: null,
    street: null,
    house_number: null,
    postal_code: null,
    city: null,
    country: null,
    vat_number: null,
    stripe_customer_id: null,
});

export type PaymentMethodType = 
    | 'card' 
    // | 'ideal' 
    | 'sepa_debit';

export type PaymentMethod = {
    stripe_payment_method_id: string | null;
    payment_type: PaymentMethodType;
    card_brand: string | null;
    card_owner_name: string | null;
    card_last_digits: string | null;
    card_exp_month: number | null;
    card_exp_year: number | null;
    sepa_bank_name: string | null;
    sepa_account_holder: string | null;
    sepa_iban_last4: string | null;
    sepa_mandate_id: string | null;
    allow_sepa: [];
};

export const createPaymentMethod = (): PaymentMethod => ({
    stripe_payment_method_id: null,
    payment_type: 'sepa_debit',
    card_brand: null,
    card_owner_name: null,
    card_last_digits: null,
    card_exp_month: null,
    card_exp_year: null,
    sepa_bank_name: null,
    sepa_account_holder: null,
    sepa_iban_last4: null,
    sepa_mandate_id: null,
    allow_sepa: [],
});